import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Space, Spin, Tabs, Tag, Typography } from 'antd';
import Layout from './Layout';
import { api, errorText, hasRole, SubContext } from './subcontracts/shared';
import DashboardTab from './subcontracts/DashboardTab';
import FormTab from './subcontracts/FormTab';
import ProblemsTab from './subcontracts/ProblemsTab';
import ActsTab from './subcontracts/ActsTab';
import { AbbreviationsTab, ExecutorsTab, SubcontractorsTab } from './subcontracts/DirectoriesTabs';
import { AuditTab, CoefficientsTab, ImportTab, SettingsTab } from './subcontracts/OtherTabs';

const { Title, Text } = Typography;

const SubcontractsPage = () => {
    const [me, setMe] = useState(null);
    const [meta, setMeta] = useState({});
    const [settings, setSettings] = useState({});
    const [subcontractors, setSubcontractors] = useState([]);
    const [error, setError] = useState(null);
    const [tab, setTab] = useState('dashboard');
    const [formPreset, setFormPreset] = useState(null);
    const [problemsPreset, setProblemsPreset] = useState(null);
    const [openActId, setOpenActId] = useState(null);
    const [actsVersion, setActsVersion] = useState(0);

    const loadSubcontractors = useCallback(async () => {
        const { data } = await api.get('/subcontractors');
        setSubcontractors(data);
    }, []);
    const loadSettings = useCallback(async () => {
        const { data } = await api.get('/settings');
        setSettings(data);
    }, []);

    useEffect(() => {
        (async () => {
            try {
                const [{ data: m }, { data: mt }] = await Promise.all([api.get('/me'), api.get('/meta')]);
                setMe(m);
                setMeta(mt);
                await Promise.all([loadSubcontractors(), loadSettings()]);
            } catch (e) {
                setError(errorText(e));
            }
        })();
    }, [loadSubcontractors, loadSettings]);

    const ctx = useMemo(() => ({ me: me || { roles: [] }, meta, settings, subcontractors }), [me, meta, settings, subcontractors]);

    if (error) {
        return (
            <Layout>
                <div style={{ padding: 20 }}><Alert type="error" showIcon message={error} /></div>
            </Layout>
        );
    }
    if (!me) {
        return (
            <Layout>
                <div style={{ padding: 60, textAlign: 'center' }}><Spin size="large" /></div>
            </Layout>
        );
    }

    const items = [
        {
            key: 'dashboard',
            label: 'Сводка',
            children: (
                <DashboardTab
                    onOpenProblems={(period, reason) => { setProblemsPreset({ period, reason, n: Date.now() }); setTab('problems'); }}
                    onOpenWorks={(period, state) => { setFormPreset({ period, state: state || 'all', n: Date.now() }); setTab('form'); }}
                />
            )
        },
        {
            key: 'form',
            label: 'Формирование',
            children: (
                <FormTab
                    preset={formPreset}
                    onFormed={created => {
                        setActsVersion(v => v + 1);
                        if (created?.length === 1) setOpenActId(created[0].id);
                        setTab('acts');
                    }}
                />
            )
        },
        { key: 'problems', label: 'Проблемы', children: <ProblemsTab preset={problemsPreset} /> },
        { key: 'acts', label: 'Акты', children: <ActsTab key={actsVersion} openActId={openActId} onOpenHandled={() => setOpenActId(null)} /> },
        { key: 'executors', label: 'Исполнители', children: <ExecutorsTab /> },
        { key: 'subcontractors', label: 'Субподрядчики', children: <SubcontractorsTab onChanged={loadSubcontractors} /> },
        { key: 'abbreviations', label: 'Сокращения', children: <AbbreviationsTab /> },
        { key: 'coefficients', label: 'Коэффициенты', children: <CoefficientsTab /> },
        ...(hasRole(me, 'sdo') ? [{ key: 'import', label: 'Импорт Excel', children: <ImportTab /> }] : []),
        { key: 'audit', label: 'Журнал', children: <AuditTab /> },
        { key: 'settings', label: 'Настройки и роли', children: <SettingsTab onChanged={loadSettings} /> }
    ];

    return (
        <Layout>
            <SubContext.Provider value={ctx}>
                <div style={{ padding: 20, maxWidth: 1900, margin: '0 auto' }}>
                    <Space align="baseline" wrap style={{ marginBottom: 4 }}>
                        <Title level={3} style={{ margin: 0 }}>Субподряды</Title>
                        <Text type="secondary">акты выполненных работ субподрядчиков по данным ВДР (доход)</Text>
                        {(me.roles || []).map(r => <Tag key={r} color="blue">{me.role_titles?.[r] || r}</Tag>)}
                    </Space>
                    <Tabs activeKey={tab} onChange={setTab} items={items} destroyInactiveTabPane={false} />
                </div>
            </SubContext.Provider>
        </Layout>
    );
};

export default SubcontractsPage;
